import { getProjectDetails } from '../../../virtco/services/data-provider';

type ProjectDetailsPayload = any;

const inflightByProjectId = new Map<string, Promise<ProjectDetailsPayload>>();
const cacheByProjectId = new Map<string, { data: ProjectDetailsPayload; fetchedAt: number }>();
/** Bumps on force refresh so stale in-flight responses do not write cache */
const generationByProjectId = new Map<string, number>();

/** Short TTL so step switches reuse data without another HTTP call */
const CACHE_TTL_MS = 10000;

function getGeneration(projectId: string): number {
  return generationByProjectId.get(projectId) ?? 0;
}

/**
 * Single-flight + short cache for getProjectDetails.
 * Multiple first/second/third step loaders share one network request per projectId.
 */
export async function getSharedProjectDetailsResponse(
  projectId: string,
  options?: { force?: boolean }
): Promise<ProjectDetailsPayload> {
  if (!projectId) {
    throw new Error('projectId is required');
  }

  if (!options?.force) {
    const cached = cacheByProjectId.get(projectId);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.data;
    }

    const inflight = inflightByProjectId.get(projectId);
    if (inflight) {
      return inflight;
    }
  } else {
    cacheByProjectId.delete(projectId);
    generationByProjectId.set(projectId, getGeneration(projectId) + 1);
  }

  const requestGeneration = getGeneration(projectId);
  let requestPromise!: Promise<ProjectDetailsPayload>;

  requestPromise = (async () => {
    try {
      const response = await getProjectDetails(projectId);
      const responseData = response.data?.data || response.data;
      if (getGeneration(projectId) === requestGeneration) {
        cacheByProjectId.set(projectId, { data: responseData, fetchedAt: Date.now() });
      }
      return responseData;
    } finally {
      const currentInflight = inflightByProjectId.get(projectId);
      if (currentInflight === requestPromise) {
        inflightByProjectId.delete(projectId);
      }
    }
  })();

  inflightByProjectId.set(projectId, requestPromise);
  return requestPromise;
}

/**
 * Drop cached payload only. Does not cancel in-flight requests so callers keep single-flight.
 * Use getSharedProjectDetailsResponse(id, { force: true }) when a fresh HTTP call is required.
 */
export function clearSharedProjectDetailsCache(projectId?: string): void {
  if (projectId) {
    cacheByProjectId.delete(projectId);
    return;
  }
  cacheByProjectId.clear();
}
